/**
 * Desktop-aware actions that need different plumbing inside the Tauri wrap than
 * in a plain browser (AB#489 checkout handoff, AB#491 ZIP save).
 *
 * Both branch on `isTauri()` and dynamically import the Tauri plugins so the web
 * bundle never loads them (same pattern as EditorDiskBar).
 */
import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { BaxterDog } from '../components/baxter/BaxterDog';
import { api, isTauri, openExternal, resolveMediaUrl, type ApiResponse } from '@wordpuppi/shared';

// AB#635: extension buckets for the `accept` attr -> Tauri dialog filter map,
// and its reverse (extension -> MIME) to reconstruct File.type after readFile.
const MIME_GROUP_EXTS: Record<string, string[]> = {
  'image/*': ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'ico', 'avif'],
  'video/*': ['mp4', 'mov', 'webm', 'm4v'],
  'audio/*': ['mp3', 'm4a', 'wav', 'ogg'],
};
const EXT_MIME: Record<string, string> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
  webp: 'image/webp', svg: 'image/svg+xml', ico: 'image/x-icon', avif: 'image/avif',
  mp4: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm', m4v: 'video/x-m4v',
  mp3: 'audio/mpeg', m4a: 'audio/mp4', wav: 'audio/wav', ogg: 'audio/ogg',
  pdf: 'application/pdf', txt: 'text/plain', md: 'text/markdown', doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

// An `<input accept>` string -> flat, deduped extension list for the dialog
// filter. `image/*` etc. expand to their bucket; `.pdf` -> `pdf`; a bare MIME
// becomes its extension when known (`image/png` -> `png`), else is ignored.
// Empty in -> empty out (no filter).
export function acceptToExtensions(accept?: string): string[] {
  if (!accept) return [];
  const out = new Set<string>();
  for (const raw of accept.split(',')) {
    const tok = raw.trim().toLowerCase();
    if (!tok) continue;
    if (MIME_GROUP_EXTS[tok]) MIME_GROUP_EXTS[tok].forEach((x) => out.add(x));
    else if (tok.startsWith('.')) out.add(tok.slice(1));
    else
      for (const [ext, mime] of Object.entries(EXT_MIME)) if (mime === tok) out.add(ext);
  }
  return [...out];
}

/**
 * AB#635 round 2 ROOT CAUSE: read a picked file's bytes in a REALM-SAFE way.
 *
 * The shell runs the admin in a same-origin IFRAME and the admin adopts the
 * shell frame's `__TAURI_INTERNALS__` (wpp-shared `utils/env.ts`) — so `invoke`
 * executes in the PARENT realm and resolves with a PARENT-realm `ArrayBuffer`.
 * `@tauri-apps/plugin-fs`'s `readFile` branches on
 *   `arr instanceof ArrayBuffer ? new Uint8Array(arr) : Uint8Array.from(arr)`
 * and `instanceof` is FALSE across realms, so it fell through to
 * `Uint8Array.from(<ArrayBuffer>)` — an ArrayBuffer is neither iterable nor
 * array-like, so that yields an EMPTY array. Every desktop pick produced a
 * 0-byte File with a correct `type`, the multipart part header said
 * `Content-Type: image/png` with no content, and the server's magic-byte gate
 * answered "File content does not match declared type 'image/png'".
 *
 * `new Uint8Array(x)` is realm-agnostic (it reads the buffer's internal slot,
 * not its prototype) and also accepts the plain `number[]` the postMessage IPC
 * fallback returns — so it is correct on every transport. Never route this
 * through `plugin-fs`'s wrapper again.
 */
async function readFileBytes(path: string) {
  const { invoke } = await import('@tauri-apps/api/core');
  // dialog->fs runtime path grant (#491) still applies: same command, same scope.
  const raw = await invoke<ArrayBuffer | number[]>('plugin:fs|read_file', { path });
  // The cast is for TS only — at runtime this constructor also accepts the
  // plain number[] that the postMessage IPC fallback (Android / blocked custom
  // protocol) returns.
  return new Uint8Array(raw as ArrayBuffer);
}

/**
 * AB#635: pick files WITHOUT letting WKWebView open the native panel.
 *
 * Root cause (macOS crash report / SIGABRT): on desktop, clicking any
 * `<input type="file">` makes WKWebView invoke wry's `runOpenPanel` delegate,
 * which runs `NSOpenPanel.runModal()` — a nested modal run loop — inside a
 * WebKit IPC dispatch on the main thread. wry 0.55.1 panics there → SIGABRT →
 * the app is killed. No upstream fix exists.
 *
 * Fix: on Tauri, intercept the click synchronously (`preventDefault`) so the
 * webview never reaches its panel, then pick + read via the thread-safe dialog
 * + fs plugins and feed the SAME `File[]` the input's `onChange` would have.
 * On web this is a no-op (returns false) — the native input is untouched.
 *
 * @returns true when it handled the pick (desktop). The caller MUST call this
 *   synchronously in `onClick` and skip the native input when it returns true.
 */
export function pickFilesDesktop(
  e: { preventDefault(): void },
  opts: { multiple?: boolean; accept?: string },
  onFiles: (files: File[]) => void,
): boolean {
  if (!isTauri()) return false; // web: native input, zero behavior change
  e.preventDefault(); // sync, before any await — stops the WKWebView panel

  void (async () => {
    try {
      const { open } = await import('@tauri-apps/plugin-dialog');
      const extensions = acceptToExtensions(opts.accept);
      const selected = (await open({
        multiple: opts.multiple ?? false,
        title: 'Choose files',
        filters: extensions.length ? [{ name: 'Files', extensions }] : undefined,
      })) as string | string[] | null;
      if (selected == null) return; // cancelled — no-op

      const paths = Array.isArray(selected) ? selected : [selected];
      const files: File[] = [];
      for (const path of paths) {
        const bytes = await readFileBytes(path);
        const name = path.split(/[\\/]/).pop() || 'file';
        const dot = name.lastIndexOf('.');
        const ext = dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
        files.push(new File([bytes], name, { type: EXT_MIME[ext] ?? 'application/octet-stream' }));
      }
      if (files.length) onFiles(files);
    } catch (err) {
      console.error('[AB#635] desktop file pick failed', err);
    }
  })();

  return true;
}

/**
 * AB#489: send the user to RevenueCat's hosted checkout.
 *
 * Desktop: hand off to the system browser (`openExternal`) so the checkout does
 * NOT navigate the webview off `tauri://localhost` — the caller keeps its
 * AB#450 poller / on-focus refetch alive to catch the entitlement flip.
 * Browser: same-window redirect, exactly as before (poller resumes on return).
 */
export async function startCheckout(url: string): Promise<void> {
  if (isTauri()) {
    await openExternal(url);
  } else {
    window.location.href = url;
  }
}

/**
 * AB#581: open a manage/store billing link.
 *
 * Desktop: system browser (`openExternal`) — same reason as startCheckout, keep
 * the webview on `tauri://localhost`. Web: a NEW tab, not a same-window nav —
 * unlike checkout, a manage link should never navigate the user off /billing.
 */
export async function openBillingLink(url: string): Promise<void> {
  if (isTauri()) {
    await openExternal(url);
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * AB#491: save a Blob to disk (AB#659: any type, not just ZIP).
 *
 * Desktop: WKWebView ignores `<a download>`/blob URLs, so pop the native save
 * dialog and write the bytes via the fs plugin. Cancelling the dialog is a
 * no-op (returns false). Browser: the existing anchor/blob download.
 *
 * @param filter native save-dialog file-type filter. Omit for no filter — pass
 *   one only when the extension is fixed, since macOS forces the filter's
 *   extension onto the saved name.
 * @returns true if written, false if the save dialog was cancelled.
 */
export async function saveBlob(
  blob: Blob,
  filename: string,
  filter?: { name: string; extensions: string[] },
): Promise<boolean> {
  if (isTauri()) {
    const { save } = await import('@tauri-apps/plugin-dialog');
    const { writeFile } = await import('@tauri-apps/plugin-fs');
    const path = await save({
      defaultPath: filename,
      filters: filter ? [filter] : undefined,
    });
    if (!path) return false; // cancelled — no-op
    const buf = await blob.arrayBuffer();
    await writeFile(path, new Uint8Array(buf));
    return true;
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return true;
}

/** AB#491 site-build ZIP save — `saveBlob` with the zip dialog filter. */
export const saveZipBlob = (blob: Blob, filename: string) =>
  saveBlob(blob, filename, { name: 'ZIP archive', extensions: ['zip'] });

/**
 * AB#533: open a content preview.
 *
 * Browser: unchanged AB#488/#530 handoff — stash the preview data in
 * localStorage and open the authed `/sites/.../preview` route in a new tab. That
 * route shares this session, so it can render via the authed `preview-full`
 * endpoint itself.
 *
 * Desktop (Tauri): `openExternal` hands off to the SYSTEM browser, which has NO
 * WordPuppi JWT and can't reach the authed preview route — the "data not found"
 * bug. So render the HTML *here* in the authed webview (via `render`), POST it to
 * the authed single-use `/api/v1/preview` mint endpoint, and open THAT url. The
 * system browser GETs it once, no session needed. A fresh key is minted on every
 * click (single-use, 5-min TTL — never cached).
 *
 * AB#661: `viaMint` takes the desktop path on the web too, for HTML the SPA
 * preview route can't reproduce from stashed page/post data (a built page).
 */
type PreviewRender = () => Promise<ApiResponse<{ html: string } | { error: string }>>;

/** Reserve the browser tab during the click, before the build consumes user activation. */
export async function openPreparedPreview(prepare: () => Promise<string>): Promise<void> {
  const desktop = isTauri();
  const tab = desktop ? null : window.open('about:blank', '_blank');
  if (!desktop && !tab) throw new Error('Allow pop-ups to open the preview.');
  let loading: ReturnType<typeof createRoot> | undefined;
  let loadingPage: Document | undefined;
  let departed = false;
  const cleanup = () => {
    // A user may have navigated the reserved tab across origins already.
    try { tab?.removeEventListener('pagehide', onPageHide); } catch { /* old document is gone */ }
    loading?.unmount();
    loading = undefined;
  };
  const onPageHide = () => { departed = true; cleanup(); };
  const stillOwnsPage = () => {
    if (!tab || tab.closed || departed) return false;
    try { return tab.document === loadingPage && tab.location.href === 'about:blank'; }
    catch { return false; }
  };
  try {
    if (tab) {
      tab.opener = null;
      const page = tab.document;
      loadingPage = page;
      tab.addEventListener('pagehide', onPageHide, { once: true });
      page.title = 'Building preview…';
      page.documentElement.className = document.documentElement.className;
      page.documentElement.lang = document.documentElement.lang || 'en';
      const base = page.createElement('base');
      base.href = document.baseURI;
      page.head.append(base);
      // Reuse the app's local fonts, theme tokens and dog keyframes in dev and production.
      document.querySelectorAll('style[data-vite-dev-id], link[rel="stylesheet"]').forEach((style) => {
        if (style.tagName === 'LINK' && new URL(style.getAttribute('href') ?? '', document.baseURI).origin !== window.location.origin) return;
        page.head.append(style.cloneNode(true));
      });
      const colors = getComputedStyle(document.documentElement);
      for (const token of ['--background', '--foreground', '--card', '--primary']) {
        page.documentElement.style.setProperty(token, colors.getPropertyValue(token));
      }
      page.body.className = 'preview-building';
      loading = createRoot(page.body);
      flushSync(() => loading!.render(createElement('main', { role: 'status' },
        createElement(BaxterDog), createElement('p', null, 'Building preview ...'))));
    }
    const url = await prepare();
    if (tab) {
      // Keep the loader painted while the destination request loads. pagehide
      // cleans it only when navigation commits; never replace a user's new page.
      if (stillOwnsPage()) tab.location.replace(url);
      else cleanup();
    } else await openExternal(url);
  } catch (error) {
    const owned = stillOwnsPage();
    cleanup();
    if (owned) tab?.close();
    throw error;
  }
}

export async function openPreview(
  opts:
    | { viaMint: true; render: PreviewRender; beforeRender?: () => Promise<void> }
    | {
        viaMint?: false;
        browserUrl: string;
        storageKey: string;
        storageValue: string;
        render: PreviewRender;
      },
): Promise<void> {
  if (!isTauri() && !opts.viaMint) {
    localStorage.setItem(opts.storageKey, opts.storageValue);
    await openExternal(opts.browserUrl);
    return;
  }

  const prepare = async () => {
    if (opts.viaMint) await opts.beforeRender?.();
    const rendered = await opts.render();
    const html = rendered.data && 'html' in rendered.data ? rendered.data.html : undefined;
    if (!html) {
      const err = rendered.data && 'error' in rendered.data ? rendered.data.error : undefined;
      throw new Error(err ?? 'Preview render failed');
    }
    const mint = await api.post<ApiResponse<{ url: string }>>('/v1/preview', { html });
    const url = mint.data?.url;
    if (!url) throw new Error('Preview link could not be created');
    // `url` is root-relative (`/preview/<key>`, outside /api). resolveMediaUrl
    // prefixes the API origin on desktop (absolute base) so the system browser
    // hits the real host, not tauri://localhost.
    return resolveMediaUrl(url);
  };
  if (opts.viaMint) await openPreparedPreview(prepare);
  else await openExternal(await prepare());
}
